import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "../../../lib/utils";
import {
  Settings,
  SlidersHorizontal,
  Eye,
  Play,
  X,
  Copy,
  Check,
  ExternalLink,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from "lucide-react";
import { Button } from "../../../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import GatewayJsonViewer from "../components/GatewayJsonViewer";
import { GatewayServersApi } from "../Api/gatewayApi";
import { formatDuration, copyToClipboard } from "../utils/gatewayUtils";
import { useToast } from "../../../../hooks/use-toast";
import type { McpTool } from "../interfaces/gatewayInterfaces";
import "../AgenticGateway.css";


interface ServerInfo {
  configKey: string;
  name: string;
  active: boolean;
}

interface FormFieldDef {
  name: string;
  type: string;
  description?: string;
  required: boolean;
  enum?: string[];
  itemType?: string;
}

type ExecutionStatus = "idle" | "running" | "success" | "error";

interface ExecutionResult {
  data: any;
  status: ExecutionStatus;
  duration: number;
  errorMessage?: string;
}


function normalizeToolOutput(raw: any): any {
  if (raw && typeof raw === "object" && Array.isArray(raw.content) && raw.content.length === 1) {
    const item = raw.content[0];
    if (item?.type === "text" && typeof item.text === "string") {
      try {
        return JSON.parse(item.text);
      } catch {
        return raw;
      }
    }
  }
  if (Array.isArray(raw) && raw.length === 1 && raw[0]?.type === "text" && typeof raw[0].text === "string") {
    try {
      return JSON.parse(raw[0].text);
    } catch {
      return raw;
    }
  }
  if (raw && typeof raw === "object" && typeof raw.text === "string") {
    try {
      return JSON.parse(raw.text);
    } catch {
      return raw;
    }
  }
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }
  return raw;
}

function schemaToFields(schema: McpTool["inputSchema"]): FormFieldDef[] {
  if (!schema?.properties) return [];
  const required = new Set(schema.required || []);
  return Object.entries(schema.properties).map(([name, prop]: [string, any]) => ({
    name,
    type: prop.type || "string",
    description: prop.description,
    required: required.has(name),
    enum: prop.enum,
    itemType: prop.items?.type,
  }));
}


interface FormValues {
  [key: string]: any;
}

function DynamicForm({
  fields,
  values,
  onChange,
}: {
  fields: FormFieldDef[];
  values: FormValues;
  onChange: (name: string, value: any) => void;
}) {
  if (fields.length === 0) {
    return (
      <div className="text-sm text-muted-foreground text-center py-8">
        This tool requires no input parameters.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {fields.map((field) => (
        <FormField key={field.name} field={field} value={values[field.name]} onChange={onChange} />
      ))}
    </div>
  );
}

function FormField({
  field,
  value,
  onChange,
}: {
  field: FormFieldDef;
  value: any;
  onChange: (name: string, value: any) => void;
}) {
  const labelEl = (
    <label className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
      {field.name}
      {field.required ? (
        <span className="text-red-600 dark:text-red-400 font-bold">*</span>
      ) : (
        <span className="text-muted-foreground/70 text-[10px] font-normal">optional</span>
      )}
    </label>
  );

  const inputCls =
    "w-full bg-card border border-border rounded-lg px-3 py-2 text-xs text-foreground placeholder-muted-foreground outline-none focus:border-primary/50 transition-colors";

  if (field.type === "boolean") {
    return (
      <div className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2.5">
        <div>
          <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
            {field.name}
            {field.required && <span className="text-red-600 dark:text-red-400 font-bold">*</span>}
          </div>
          {field.description && (
            <p className="text-[10px] text-muted-foreground/70 mt-0.5">{field.description}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => onChange(field.name, !value)}
          className={cn(
            "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200",
            value ? "bg-primary" : "bg-muted-foreground/35"
          )}
        >
          <span
            className={cn(
              "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200",
              value ? "translate-x-4" : "translate-x-0"
            )}
          />
        </button>
      </div>
    );
  }

  if (field.enum && field.enum.length > 0) {
    return (
      <div className="space-y-1.5">
        {labelEl}
        {field.description && (
          <p className="text-[10px] text-muted-foreground/70">{field.description}</p>
        )}
        <select
          value={value ?? ""}
          onChange={(e) => onChange(field.name, e.target.value || undefined)}
          className={inputCls}
        >
          <option value="">-- select --</option>
          {field.enum!.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === "number" || field.type === "integer") {
    return (
      <div className="space-y-1.5">
        {labelEl}
        {field.description && (
          <p className="text-[10px] text-muted-foreground/70">{field.description}</p>
        )}
        <input
          type="number"
          value={value ?? ""}
          onChange={(e) =>
            onChange(field.name, e.target.value === "" ? undefined : parseFloat(e.target.value))
          }
          placeholder={field.name}
          className={inputCls}
        />
      </div>
    );
  }

  if (field.type === "object" || field.type === "array") {
    return (
      <div className="space-y-1.5">
        {labelEl}
        {field.description && (
          <p className="text-[10px] text-muted-foreground/70">{field.description}</p>
        )}
        <textarea
          value={typeof value === "string" ? value : value !== undefined ? JSON.stringify(value, null, 2) : ""}
          onChange={(e) => onChange(field.name, e.target.value)}
          placeholder={field.type === "array" ? "[]" : "{}"}
          rows={4}
          className={cn(inputCls, "resize-y font-mono")}
        />
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {labelEl}
      {field.description && (
        <p className="text-[10px] text-muted-foreground/70">{field.description}</p>
      )}
      <input
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(field.name, e.target.value)}
        placeholder={field.name}
        className={inputCls}
      />
    </div>
  );
}


export default function GatewayPlaygroundPage() {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [servers, setServers] = useState<ServerInfo[]>([]);
  const [serversLoading, setServersLoading] = useState(true);
  const [serversError, setServersError] = useState<string | null>(null);

  const [selectedServerKey, setSelectedServerKey] = useState("");
  const [tools, setTools] = useState<McpTool[]>([]);
  const [toolsLoading, setToolsLoading] = useState(false);

  const [selectedToolIndex, setSelectedToolIndex] = useState<number | null>(null);
  const selectedTool = selectedToolIndex !== null ? tools[selectedToolIndex] ?? null : null;

  const [formValues, setFormValues] = useState<FormValues>({});
  const formFields: FormFieldDef[] = selectedTool ? schemaToFields(selectedTool.inputSchema) : [];

  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState<ExecutionResult | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      setServersLoading(true);
      try {
        const data = await GatewayServersApi.list();
        let serverList: ServerInfo[] = [];
        if (data && typeof data === "object" && !Array.isArray(data)) {
          serverList = Object.entries(data).map(([key, info]: [string, any]) => ({
            configKey: key,
            name: info.serverName || info.name || key,
            active: info.status === "connected" || info.active === true,
          }));
        } else if (Array.isArray(data)) {
          serverList = data.map((s: any) => ({
            configKey: s.configKey || s.name,
            name: s.serverName || s.name || s.configKey,
            active: s.status === "connected" || s.active === true,
          }));
        }
        setServers(serverList.filter((s) => s.active));
      } catch (err: any) {
        setServersError(err?.message || "Failed to load servers");
      } finally {
        setServersLoading(false);
      }
    })();
  }, []);

  const handleServerChange = async (key: string) => {
    setSelectedServerKey(key);
    setTools([]);
    setSelectedToolIndex(null);
    setFormValues({});
    setResult(null);

    if (!key) return;
    setToolsLoading(true);
    try {
      const toolList = await GatewayServersApi.tools(key);
      setTools(Array.isArray(toolList) ? toolList : []);
    } catch (err: any) {
      toast({ title: "Failed to load tools", description: err?.message, variant: "destructive" });
    } finally {
      setToolsLoading(false);
    }
  };

  const handleToolChange = (indexStr: string) => {
    if (!indexStr) {
      setSelectedToolIndex(null);
      setFormValues({});
      return;
    }
    const idx = parseInt(indexStr, 10);
    setSelectedToolIndex(idx);
    setFormValues({});
    setResult(null);
  };

  const handleFieldChange = (name: string, value: any) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const collectInput = useCallback(() => {
    const input: Record<string, any> = {};
    formFields.forEach((field) => {
      const val = formValues[field.name];
      if (val === undefined || val === "") return;
      if (field.type === "object" || field.type === "array") {
        if (typeof val === "string" && val.trim()) {
          try {
            input[field.name] = JSON.parse(val);
          } catch {
            input[field.name] = val;
          }
        } else if (val !== undefined && val !== "") {
          input[field.name] = val;
        }
      } else {
        input[field.name] = val;
      }
    });
    return input;
  }, [formFields, formValues]);

  const handleExecute = async () => {
    if (!selectedTool || !selectedServerKey) return;
    const input = collectInput();
    setExecuting(true);
    setResult(null);
    const startTime = Date.now();
    try {
      const raw = await GatewayServersApi.callTool(selectedServerKey, selectedTool.name, input);
      const duration = Date.now() - startTime;
      const normalized = normalizeToolOutput(raw);
      setResult({ data: normalized, status: "success", duration });
    } catch (err: any) {
      const duration = Date.now() - startTime;
      setResult({
        data: null,
        status: "error",
        duration,
        errorMessage: err?.message || "Unknown error",
      });
    } finally {
      setExecuting(false);
    }
  };

  const handleClear = () => {
    setFormValues({});
    setResult(null);
  };

  const handleCopyOutput = async () => {
    if (!result) return;
    const text =
      result.status === "error"
        ? result.errorMessage || ""
        : typeof result.data === "string"
        ? result.data
        : JSON.stringify(result.data, null, 2);
    await copyToClipboard(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({ title: "Output copied to clipboard" });
  };

  if (serversLoading) {
    return (
      <div className="ag-page">
        <div className="ag-card ag-content">
          <div className="ag-loading h-full">
            <span className="ag-spinner animate-spin" aria-hidden="true" />
            <span>Loading servers…</span>
          </div>
        </div>
      </div>
    );
  }

  if (serversError) {
    return (
      <div className="ag-page">
        <div className="ag-card ag-content">
          <div className="ag-empty h-full">
            <span className="ag-empty-icon text-red-600 dark:text-red-400" aria-hidden="true">
              <AlertCircle />
            </span>
            <p>{serversError}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ag-page">
    <div className="flex gap-6 flex-1 min-h-[560px]">
      <aside className="w-[280px] shrink-0 flex flex-col gap-4">
        <div className="ag-card p-4 flex flex-col gap-4 flex-1">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <Settings className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold text-foreground/80">Configuration</h3>
            </div>
            <p className="text-[10px] text-muted-foreground/70">Select target server and tool.</p>
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
              Server
            </label>
            {servers.length === 0 ? (
              <p className="text-xs text-muted-foreground/70">No connected servers available</p>
            ) : (
              <Select value={selectedServerKey} onValueChange={handleServerChange}>
                <SelectTrigger className="ag-select h-10 text-xs">
                  <SelectValue placeholder="— Select Server —" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {servers.map((s) => (
                    <SelectItem key={s.configKey} value={s.configKey} className="text-xs">
                      {s.name || s.configKey}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
              Tool
            </label>
            {toolsLoading ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <RefreshCw className="w-3 h-3 animate-spin" /> Loading tools...
              </div>
            ) : (
              <Select
                value={selectedToolIndex !== null ? String(selectedToolIndex) : ""}
                onValueChange={handleToolChange}
                disabled={!selectedServerKey || tools.length === 0}
              >
                <SelectTrigger className="ag-select h-10 text-xs">
                  <SelectValue placeholder={selectedServerKey ? "— Select Tool —" : "Select a server first"} />
                </SelectTrigger>
                <SelectContent className="bg-card border-border max-h-[260px]">
                  {tools.map((t, i) => (
                    <SelectItem key={t.name} value={String(i)} className="text-xs">
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {selectedTool?.description && (
            <div className="rounded-lg border border-border ag-surface px-3 py-2.5">
              <p className="text-[10px] text-muted-foreground leading-relaxed">{selectedTool.description}</p>
            </div>
          )}

          <p className="text-[10px] text-muted-foreground/70 mt-auto pt-2">
            Dynamic form fields are generated from each tool's input schema.
          </p>
        </div>
      </aside>

      {/* ── Middle panel: Input ── */}
      <section className="flex-1 min-w-0 flex flex-col">
        <div className="ag-card p-4 flex flex-col gap-4 flex-1 overflow-auto">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <SlidersHorizontal className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold text-foreground/80">Input Parameters</h3>
            </div>
            <p className="text-[10px] text-muted-foreground/70">Configure payload variables for the selected tool execution.</p>
          </div>

          {!selectedTool ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground/70 text-sm">
              Select a server and tool to configure inputs
            </div>
          ) : (
            <div className="flex-1 overflow-auto">
              <DynamicForm
                fields={formFields}
                values={formValues}
                onChange={handleFieldChange}
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2 border-t border-border">
            <Button
              onClick={handleExecute}
              disabled={!selectedTool || executing}
              className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-[0_4px_8px_-2px_hsl(var(--primary)/0.25)]"
              size="sm"
            >
              {executing ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Executing...
                </>
              ) : (
                <>
                  <Play className="w-3.5 h-3.5 mr-1.5" /> Execute
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClear}
              disabled={!selectedTool}
              className="border-border text-muted-foreground hover:text-foreground/80 text-xs"
            >
              <X className="w-3.5 h-3.5 mr-1.5" /> Clear
            </Button>
          </div>
        </div>
      </section>

      {/* ── Right panel: Output ── */}
      <section className="flex-1 min-w-0 flex flex-col">
        <div className="ag-card p-4 flex flex-col gap-4 flex-1">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <Eye className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold text-foreground/80">Output Preview</h3>
            </div>
            <p className="text-[10px] text-muted-foreground/70">Formatted response with readable JSON structure.</p>
          </div>

          {/* Result area */}
          <div className="flex-1 overflow-auto">
            {!result && !executing ? (
              <div className="flex items-center justify-center h-full text-muted-foreground/70 text-sm">
                Select a server and tool, then execute to see results.
              </div>
            ) : executing ? (
              <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground text-sm">
                <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                Executing tool call...
              </div>
            ) : result?.status === "error" ? (
              <div className="space-y-3">
                <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-3">
                  <p className="text-xs text-red-300 leading-relaxed">{result.errorMessage}</p>
                </div>
                {/* Status + duration */}
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/20 font-medium">
                    <XCircle className="w-3.5 h-3.5" /> ERROR
                  </span>
                  <span className="text-xs text-muted-foreground">{formatDuration(result.duration)}</span>
                </div>
              </div>
            ) : result?.status === "success" ? (
              <div className="space-y-3">
                <GatewayJsonViewer
                  data={result.data}
                  label="Result"
                  collapsed={false}
                  maxHeight="400px"
                />
                {/* Status + duration */}
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 font-medium">
                    <CheckCircle2 className="w-3.5 h-3.5" /> SUCCESS
                  </span>
                  <span className="text-xs text-muted-foreground">{formatDuration(result.duration)}</span>
                </div>
              </div>
            ) : null}
          </div>

          {/* Output actions */}
          <div className="pt-2 border-t border-border flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyOutput}
              disabled={!result}
              className="border-border text-muted-foreground hover:text-foreground/80 text-xs"
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5 mr-1.5 text-emerald-600 dark:text-emerald-400" /> Copied!
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy Output
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/agentic-gateway/audit")}
              disabled={!result}
              className="border-border text-muted-foreground hover:text-foreground/80 text-xs"
            >
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> View in Audit Log
            </Button>
          </div>
        </div>
      </section>
    </div>
    </div>
  );
}
